import React, { useMemo, useState, useEffect } from "react";
import { Link, useOutletContext, useLocation } from "react-router-dom";
import {
  Building2,
  Coins,
  LayoutGrid,
  LockKeyhole,
  MapPin,
  UserRound, 
  Users,
  Timer,
  Share2,
  Bookmark,
  Search,
  Plus,
  CirclePlay,
  Lock,
  FileText,
} from "lucide-react";

import photolg from "../../assets/img/photo-1.jpg";
import userimg from "../../assets/img/user-img.png";
import star from "../../assets/img/star.svg";

import company1 from "../../assets/img/company1.svg";
import company2 from "../../assets/img/company2.svg";
import bookicon from "../../assets/img/bookicon.webp";


import DraftSurvey from "../../components/dashboard/DraftSurvey";
import ArchivedSurveys from "../../components/dashboard/ArchivedSurveys";
import ClosedSurvey from "../../components/dashboard/ClosedSurvey";
import ActiveSurveys from "../../components/dashboard/ActiveSurveys";
import { getMySurveys } from "../../service/mySurvey";
import SurveySkeletonLoader from "../../components/loaders/SurveySkeletonLoader";


const initialSearchTabs = [
  {
    id: "activeSurveys",
    label: "Active",
    count: 0,
    icon: CirclePlay,
  },
  {
    id: "closedSurvey",
    label: "Closed",
    count: 0,
    icon: Lock,
  },
  {
    id: "archivedSurveys",
    label: "Archive",
    count: 0,
    icon: Bookmark,
  },  
  {
    id: "draftSurvey",
    label: "Draft",
    count: 0,
    icon: FileText,
  },
];


const MySurveys = () => {
  const [activeTab, setActiveTab] = useState("activeSurveys");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTabs, setSearchTabs] = useState(initialSearchTabs);
  
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);

  const location = useLocation();

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const activeTabConfig = useMemo(
    () => searchTabs.find((tab) => tab.id === activeTab) ?? searchTabs[0],
    [activeTab, searchTabs]
  );

  const { setSearchActiveTab } = useOutletContext();

  useEffect(() => {
    setSearchActiveTab(activeTab);
  }, [activeTab]);

  const fetchSurveys = async (reset = false) => {
    let status = null;
    if (activeTab === "activeSurveys") status = 1;
    else if (activeTab === "closedSurvey") status = 2;
    else if (activeTab === "archivedSurveys") status = 3;
    else if (activeTab === "draftSurvey") status = 0;

    try {
      setLoading(true);
      if (reset) {
        setSurveys([]);
      }
      const cursor = reset ? null : nextCursor;
      const response = await getMySurveys({ status, cursor, search: searchQuery.trim() });
      
      if (response && response.data && response.data.success) {
        const newData = response.data.data || [];
        setSurveys(prev => reset ? newData : [...prev, ...newData]);
        
        const pagination = response.data.pagination;
        if (pagination) {
          setHasMore(pagination.hasMore);
          setNextCursor(pagination.nextCursor);
        } else {
          setHasMore(false);
          setNextCursor(null);
        }

        const counts = response.data.counts;
        if (counts) {
          setSearchTabs(prevTabs => prevTabs.map(tab => {
            if (tab.id === "activeSurveys") return { ...tab, count: counts.active_surveys || 0 };
            if (tab.id === "closedSurvey") return { ...tab, count: counts.closed_surveys || 0 };
            if (tab.id === "archivedSurveys") return { ...tab, count: counts.deleted_surveys || 0 };
            if (tab.id === "draftSurvey") return { ...tab, count: counts.draft_surveys || 0 };
            return tab;
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching surveys:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveys(true);
  }, [activeTab, searchQuery]);

  const handleLoadMore = () => {
    fetchSurveys(false);
  };

  return (
    <div className="dashboard-search-results">     

      <div className="row g-3">

        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            
              <div className="d-flex align-items-center justify-content-between">
                <h1 className="sr-page-title">My Surveys</h1>

                <Link to="/create-survey"
                  className="w-24 fw-bold ft-14 text-white px-4 py-3 bgred rounded-2 cursor-target text-center hover:tw-bg-[#333] tw-transition-all tw-duration-300 tw-ease-in-out hover:tw-scale-105"
                > <span className="d-flex gap-2 align-items-center"><Plus size={18} /> Create Survey</span>
                </Link>
               
              </div>              

           
          </div>
        </div>


        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            <div className="row g-3">            

              <div className="col-12">
                <div className="sr-tabs-shell px-3">
                  <div className="d-flex flex-nowrap sr-tabs-wrapper justify-content-between">
                    {searchTabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <div className="flex-shrink-0 me-2" key={tab.id}>
                          <button
                            type="button"
                            className={`sr-tab-btn ${activeTab === tab.id ? "active" : ""}`}
                            onClick={() => setActiveTab(tab.id)}
                          >
                            <Icon size={14} />
                            <span>{tab.label}</span>
                            <span className="sr-tab-count">({tab.count})</span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="col-12">
                 <div className="search-survey-box position-relative w-100">
                  <Search size={18} className="position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary" />

                   <input
                    type="text"
                    placeholder="Search Survey......"
                    className="ft-14 fw-400 w-full form-control tw-h-18 py-3 !tw-pl-[40px] !tw-bg-[#f5f5f5] border-bottom-0 !focus:tw-border-b-0 shadow-none rounded-3"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                  />
                
                </div>
              </div>


            </div>
          </div>
        </div>


        <div className="col-12 dashboard-search-results">
          {/* {loading && surveys.length === 0 ? (
            <SurveySkeletonLoader />
          ) : (
            <>
              {activeTab === "activeSurveys" && (
                <ActiveSurveys surveys={surveys} loading={loading} hasMore={hasMore} onLoadMore={handleLoadMore} onRefresh={() => fetchSurveys(true)} />
              )}

              {activeTab === "closedSurvey" && (
                <ClosedSurvey surveys={surveys} loading={loading} hasMore={hasMore} onLoadMore={handleLoadMore} onRefresh={() => fetchSurveys(true)} />
              )}

              {activeTab === "archivedSurveys" && (
                <ArchivedSurveys surveys={surveys} loading={loading} hasMore={hasMore} onLoadMore={handleLoadMore} onRefresh={() => fetchSurveys(true)} />
              )}

              {activeTab === "draftSurvey" && (
                <DraftSurvey surveys={surveys} loading={loading} hasMore={hasMore} onLoadMore={handleLoadMore} onRefresh={() => fetchSurveys(true)} />
              )}
            </>
          )} */}



          {loading ? (
              <SurveySkeletonLoader />
            ) : surveys.length === 0 ? (
              <div className="sr-result-card">
                <div className="sfm-empty-state text-center p-5">
                  <div className="surveys-for-me-page">
                    <div className="sfm-empty-icon d-flex justify-content-center">
                      <img src={bookicon} alt="bookicon"  className="img-fluid empty-icon-img" />
                    </div>
                  </div>

                  <h3 className="ft-18 fw-bold mb-2">
                    {activeTab === "activeSurveys" && "No Active Surveys"}
                    {activeTab === "closedSurvey" && "No Closed Surveys"}
                    {activeTab === "archivedSurveys" && "No Archived Surveys"}
                    {activeTab === "draftSurvey" && "No Draft Surveys"}
                  </h3>

                  <p className="ft-13 color858c95">
                    {activeTab === "activeSurveys" &&
                      "You don't have any active surveys."}

                    {activeTab === "closedSurvey" &&
                      "You don't have any closed surveys."}

                    {activeTab === "archivedSurveys" &&
                      "You don't have any archived surveys."}

                    {activeTab === "draftSurvey" &&
                      "You don't have any draft surveys."}
                  </p>
                </div>
              </div>
            ) : (
              <>
                {activeTab === "activeSurveys" && (
                  <ActiveSurveys
                    surveys={surveys}
                    loading={loading}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMore}
                    onRefresh={() => fetchSurveys(true)}
                    onSwitchTab={setActiveTab}
                  />
                )}

                {activeTab === "closedSurvey" && (
                  <ClosedSurvey
                    surveys={surveys}
                    loading={loading}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMore}
                    onRefresh={() => fetchSurveys(true)}
                    onSwitchTab={setActiveTab}
                  />
                )}

                {activeTab === "archivedSurveys" && (
                  <ArchivedSurveys
                    surveys={surveys}
                    loading={loading}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMore}
                    onRefresh={() => fetchSurveys(true)}
                    onSwitchTab={setActiveTab}
                  />
                )}

                {activeTab === "draftSurvey" && (
                  <DraftSurvey
                    surveys={surveys}
                    loading={loading}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMore}
                    onRefresh={() => fetchSurveys(true)}
                    onSwitchTab={setActiveTab}
                  />
                )}
              </>
            )}
        </div>

        


      </div>
    </div>
  );
};

export default MySurveys;
